import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  FiX, FiEye, FiLock, FiCheckCircle, FiClock, FiUser,
  FiShield, FiChevronDown, FiAlertCircle, FiDownload,
} from 'react-icons/fi';
import { documentsAPI, signingAPI } from '../../services/api';
import './DocumentViewer.scss';

/**
 * In-app read-only document viewer.
 *
 * Streams the server's PDF rendering of a locked draft, a signed copy, or a
 * historical version (DOCX versions are converted server-side) and shows it
 * inline — no download required. For signed documents it also loads the
 * contract's digital-signature summary and presents it in a panel above the
 * document, without touching the document itself.
 *
 * <DocumentViewer
 *   open={!!viewerTarget}
 *   contractId={id}
 *   documentId={viewerTarget.docId}
 *   versionNumber={viewerTarget.version}   // optional — historical version
 *   title={viewerTarget.title}
 *   badge="signed" | "locked" | null       // header badge
 *   showSignature={viewerTarget.signed}    // fetch + show digital signature info
 *   allowDownload={canDownload}            // show a download button in the header
 *   downloadName="contract_v2.pdf"         // file name for that download
 *   onClose={() => setViewerTarget(null)}
 * />
 */
function DocumentViewer({
  open,
  contractId,
  documentId,
  versionNumber = null,
  title,
  badge = null,
  showSignature = false,
  allowDownload = false,
  downloadName = null,
  onClose,
}) {
  const { t, i18n } = useTranslation();
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signature, setSignature] = useState(null);
  const [sigOpen, setSigOpen] = useState(true);

  // Load the PDF rendering whenever the target changes.
  useEffect(() => {
    if (!open || !contractId || !documentId) return undefined;
    let cancelled = false;
    let createdUrl = null;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    documentsAPI.viewPdf(contractId, documentId, versionNumber)
      .then(({ data }) => {
        if (cancelled) return;
        createdUrl = window.URL.createObjectURL(
          new Blob([data], { type: 'application/pdf' })
        );
        setBlobUrl(createdUrl);
      })
      .catch(async (err) => {
        if (cancelled) return;
        // Blob error responses need decoding before the message is readable.
        let message = null;
        try {
          const text = await err.response?.data?.text?.();
          message = text ? JSON.parse(text).error : null;
        } catch { /* fall back to the generic message */ }
        setError(message || t('documentViewer.failedToLoad', { defaultValue: 'The document could not be loaded for viewing.' }));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [open, contractId, documentId, versionNumber, t]);

  // Digital-signature summary — only relevant for signed copies.
  useEffect(() => {
    if (!open || !showSignature || !contractId) return undefined;
    let cancelled = false;
    signingAPI.signatureInfo(contractId)
      .then(({ data }) => { if (!cancelled) setSignature(data.signature); })
      .catch(() => { if (!cancelled) setSignature(null); });
    return () => { cancelled = true; };
  }, [open, showSignature, contractId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const signers = useMemo(() => {
    if (!signature) return [];
    return (signature.documents || []).flatMap((d) => d.signers || []);
  }, [signature]);

  // A manual upload carries no digital signature — the panel stays hidden.
  const hasDigitalSignature = !!signature && signature.method !== 'manual' && signers.length > 0;

  if (!open) return null;

  const formatDateTime = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return '—'; }
  };

  const methodLabel = signature?.method === 'docusign'
    ? `DocuSign${signature.docusignTemplateName ? ` · ${signature.docusignTemplateName}` : ''}`
    : t('documentViewer.internalSignature', { defaultValue: 'Electronic signature (in-app)' });

  return createPortal(
    <div className="docviewer__overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="docviewer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="docviewer__header">
          <div className="docviewer__header-title">
            <FiEye size={16} />
            <h2 title={title}>{title}</h2>
            {versionNumber != null && (
              <span className="docviewer__badge docviewer__badge--version">v{versionNumber}</span>
            )}
            {badge === 'signed' && (
              <span className="docviewer__badge docviewer__badge--signed">
                <FiCheckCircle size={11} /> {t('documentViewer.signedBadge', { defaultValue: 'Signed' })}
              </span>
            )}
            {badge === 'locked' && (
              <span className="docviewer__badge docviewer__badge--locked">
                <FiLock size={11} /> {t('documentViewer.lockedBadge', { defaultValue: 'Locked' })}
              </span>
            )}
            <span className="docviewer__badge docviewer__badge--readonly">
              {t('documentViewer.readOnly', { defaultValue: 'Read-only' })}
            </span>
          </div>
          <div className="docviewer__header-actions">
            {allowDownload && blobUrl && (
              <button
                type="button"
                className="docviewer__download"
                onClick={() => {
                  // The blob is already fetched — just hand it to the browser.
                  const link = document.createElement('a');
                  link.href = blobUrl;
                  link.download = downloadName || `${title || 'document'}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                }}
              >
                <FiDownload size={14} /> {t('documentViewer.download', { defaultValue: 'Download' })}
              </button>
            )}
            <button type="button" className="docviewer__close" onClick={onClose} aria-label={t('common.close', { defaultValue: 'Close' })}>
              <FiX size={18} />
            </button>
          </div>
        </div>

        {showSignature && hasDigitalSignature && (
          <div className="docviewer__sig">
            <button type="button" className="docviewer__sig-head" onClick={() => setSigOpen(!sigOpen)} aria-expanded={sigOpen}>
              <FiShield size={14} />
              <span>{t('documentViewer.signatureInfoTitle', { defaultValue: 'Digital Signature Information' })}</span>
              <span className="docviewer__sig-status">
                <FiCheckCircle size={12} />
                {t('documentViewer.signatureCompleted', { defaultValue: 'Signed / Completed' })}
              </span>
              <FiChevronDown className={`docviewer__sig-chevron${sigOpen ? ' docviewer__sig-chevron--open' : ''}`} />
            </button>
            {sigOpen && (
              <div className="docviewer__sig-body">
                <div className="docviewer__sig-meta">
                  <span>
                    <strong>{t('documentViewer.signatureMethod', { defaultValue: 'Method' })}:</strong> {methodLabel}
                  </span>
                  <span>
                    <strong>{t('documentViewer.signatureDate', { defaultValue: 'Completed' })}:</strong> {formatDateTime(signature.completedAt)}
                  </span>
                </div>
                <div className="docviewer__sig-signers">
                  {signers.map((s, idx) => (
                    <div key={`${s.email}-${idx}`} className="docviewer__sig-signer">
                      <span className="docviewer__sig-signer-avatar"><FiUser size={13} /></span>
                      <span className="docviewer__sig-signer-info">
                        <span className="docviewer__sig-signer-name">{s.name}</span>
                        <span className="docviewer__sig-signer-email">{s.email}</span>
                      </span>
                      <span className={`docviewer__sig-signer-state docviewer__sig-signer-state--${s.status}`}>
                        {s.status === 'signed'
                          ? <><FiCheckCircle size={12} /> {t('documentViewer.signerSigned', { defaultValue: 'Signed' })}{s.signedAt && ` · ${formatDateTime(s.signedAt)}`}</>
                          : <><FiClock size={12} /> {t(`documentViewer.signerStatus.${s.status}`, { defaultValue: s.status })}</>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="docviewer__body">
          {loading ? (
            <div className="docviewer__state">
              <div className="docviewer__spinner" />
              <span>{t('documentViewer.loading', { defaultValue: 'Preparing document…' })}</span>
            </div>
          ) : error ? (
            <div className="docviewer__state docviewer__state--error">
              <FiAlertCircle size={22} />
              <span>{error}</span>
            </div>
          ) : blobUrl ? (
            // Without download permission the browser PDF toolbar must go too —
            // its built-in download/print buttons would bypass the permission
            // (PDF open params; honoured by Chromium's viewer).
            <iframe
              src={allowDownload ? blobUrl : `${blobUrl}#toolbar=0&navpanes=0`}
              title={title}
              className="docviewer__frame"
            />
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default DocumentViewer;
